import { Component, OnInit, OnDestroy } from '@angular/core';
import { DatePipe } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, Observable } from "rxjs";
import { map, tap, finalize } from "rxjs/operators";
//import { GridOptions, ColDef } from "@ag-grid-enterprise/all-modules";
import { GridOptions, ColDef } from "ag-grid-community";
import { LoaderService } from "../../../../common/services/loader.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { WellService } from "../../../../common/services/well.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { AuthService } from "../../../../services/auth.service";
import { BulkWellStatusChange, BulkWellStatusChangeDetails, BulkWellStatusChangeSearchSpec, BulkWellStatusChangeStatus } from "../../../models/bulk-well-status-change.model";
import { IUploadFile } from "../../../../common/services/document-management.service";

@Component({
    selector: 'app-approve-bulk-well-update',
    templateUrl: './approve-bulk-well-update.component.html',
    styleUrls: ['./approve-bulk-well-update.component.scss'],
    standalone: false
})
export class ApproveBulkWellUpdateComponent implements OnInit, OnDestroy {

    confirmApproveDialog: Dialog = new Dialog("This action cannot be undone, and will make permanent changes to all well(s) in the request.  Do you wish to proceed?", [new DialogButton("No", false, () => { return; }), new DialogButton("Yes", true, () => { this.confirmDecision(BulkWellStatusChangeStatus.Approved); })]);
    confirmRejectDialog: Dialog = new Dialog("This action cannot be undone.  Are you sure you want to reject this request?", [new DialogButton("No", false, () => { return; }), new DialogButton("Yes", true, () => this.confirmDecision(BulkWellStatusChangeStatus.Rejected))]);


    get dataColumns(): ColDef[] {
        return [
            {
                headerClass: "wrapped",
                headerName: "API #",
                headerTooltip: "API #",
                field: "apiNumber",
                tooltipField: "apiNumber",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 125,
                maxWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Well Name",
                headerTooltip: "Well Name",
                field: "wellName",
                tooltipField: "wellName",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110,
                maxWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Well Number",
                headerTooltip: "Well Number",
                field: "wellNumber",
                tooltipField: "wellNumber",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 110,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "From Well Status",
                headerTooltip: "From Well Status",
                field: "fromWellStatus",
                tooltipField: "fromWellStatus",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 110,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "To Disposition",
                headerTooltip: "To Disposition",
                field: "toDisposition",
                tooltipField: "toDisposition",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 125,
                minWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "To Status Reason",
                headerTooltip: "To Status Reason",
                field: "toStatusReason",
                tooltipField: "toStatusReason",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 110,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "To Well Type Description",
                headerTooltip: "To Well Type Description",
                field: "toTypeDescription",
                tooltipField: "toTypeDescription",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 135,
                minWidth: 135
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Map Symbol",
                headerTooltip: "Map Symbol",
                field: "mapSymbol",
                tooltipField: "mapSymbol",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 110,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "New Status Date",
                headerTooltip: "New Status Date",
                field: "toStatusDate",
                tooltipValueGetter: this.formatDate("toStatusDate", true),
                valueFormatter: this.formatDate("toStatusDate", true),
                suppressMovable: true,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                maxWidth: 125,
                minWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Date Uploaded",
                headerTooltip: "Date Uploaded",
                field: "dateUploaded",
                tooltipValueGetter: this.formatDate("dateUploaded", true),
                valueFormatter: this.formatDate("dateUploaded", true),
                suppressMovable: true,
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                maxWidth: 125,
                minWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Notes",
                headerTooltip: "Notes",
                field: "notes",
                tooltipField: "notes",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 300
            } as ColDef
        ] as ColDef[];
    }


    data: BulkWellStatusChangeDetails[];

    fileHelpText: string = "Original uploaded file(s) may contain rows that were rejected due to inability to locate the associated well, or inaccurate current state of the provided well.";

    gridOptions: GridOptions;

    notReadyDialog: Dialog = new Dialog("Please wait for the request to finish loading before issuing a decision.", [new DialogButton("OK", false, () => { return; })]);

    request: BulkWellStatusChange | null;
    requestId: string;

    requestDecisionedDialog: Dialog = new Dialog("This request has been [DECISION]", [new DialogButton("OK", true, () => this.home())]);

    private subheader: SubHeader = new SubHeader({ title: "Approve Bulk Well Updates", buttons: [{ name: "Cancel", method: () => this.home() }, { name: "Reject", method: () => this.reject() }, { name: "Approve", method: () => this.approve() }] });

    subscription: Subscription = new Subscription();

    private titleText: string = "Approve Bulk Well Updates - [ID]";



    uploadedFiles: IUploadFile[] = [];


    constructor(private route: ActivatedRoute, private router: Router, private auth: AuthService, private subheaderService: SubHeaderService, private loader: LoaderService, private dialogService: DynamicDialogService,
            private wellService: WellService, private formatter: GridFormatterService, private datePipe: DatePipe) {
    }

    ngOnInit() {
        this.requestId = this.route.snapshot.params.id;
        this.subheader.title = this.titleText.replace("[ID]", this.requestId);
        this.subheaderService.updateSubHeader(this.subheader);
        let data$ = this.getRequest().pipe(
            tap((data: BulkWellStatusChange | null) => {
                if (data != null) {
                    data.bulkWellStatusChangeFiles.forEach(f => {
                        let file: IUploadFile = {
                            rawFile: null,
                            completed: true,
                            canBeDeleted: false,
                            sentToOnbase: false,
                            error: false,
                            isUploading: false,
                            waiting: false,
                            percentage: 100,
                            path: "",
                            fileName: f.fileName,
                            type: f.type,
                            blobUri: f.blobUri
                        } as IUploadFile;
                        this.uploadedFiles.push(file);
                    });
                }
            })
            ).subscribe({
                next: (data: BulkWellStatusChange | null) => {
                    this.request = data;
                    if (this.request != null) {
                        this.data = this.request.bulkWellStatusChangeDetails;
                    }                    
                },
                error: (error) => {
                    this.handleError(error);
                }
        });
        this.subscription.add(data$);
        this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.dataColumns, false, true, true, true);
        this.gridOptions.onGridReady = (params) => {
            params.api.autoSizeAllColumns();
        }
        this.gridOptions.headerHeight = 50;
        this.gridOptions.pagination = false;

    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    approve(): void {
        let dialog: Dialog = (this.request == null)
            ? this.notReadyDialog
            : this.confirmApproveDialog;
        dialog.toggleDialog(true);
        this.dialogService.updateDialog(dialog);
    }    

    confirmDecision(status: BulkWellStatusChangeStatus): void {
        this.request.status = status;
        if (status === BulkWellStatusChangeStatus.Rejected) {
            this.request.rejectedById = this.auth.User.id;
            this.request.rejectedByName = this.auth.User.displayName;
        } else if (status === BulkWellStatusChangeStatus.Approved) {
            this.request.approvedById = this.auth.User.id;
            this.request.approvedByName = this.auth.User.displayName;
        }
        
        this.loader.toggleLoader(true);
        let save$ = this.wellService.saveBulkWellStatusChange(this.request).pipe(
            finalize(() => { this.loader.toggleLoader(false); })
            ).subscribe({
                next: () => {
                    if (status === BulkWellStatusChangeStatus.Approved) this.requestDecisionedDialog.message = this.requestDecisionedDialog.message.replace('[DECISION]', 'approved');
                    else if (status === BulkWellStatusChangeStatus.Rejected) this.requestDecisionedDialog.message = this.requestDecisionedDialog.message.replace('[DECISION]', 'rejected');
                    this.requestDecisionedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.requestDecisionedDialog);
                },
                error: (error) => {
                    this.handleError(error);
                }
        });
        this.subscription.add(save$);
    }

    formatDate(field: string, formatted: boolean): any {
        return (obj: any) => {
            let date: Date | null = null;
            if (obj != null && obj.data != null && obj.data[field] != null) {
                date = <Date>obj.data[field];
            }
            if (formatted) {
                if (date != null) {
                    return this.datePipe.transform(date, "MM/dd/yyyy", '+0000');
                }
                return "N/A";
            }
            return date;
        }
    }


    getRequest(): Observable<BulkWellStatusChange | null> {
        let spec: BulkWellStatusChangeSearchSpec = {
            changeId: this.requestId
        } as BulkWellStatusChangeSearchSpec;
        return this.wellService.searchBulkWellStatusChanges(spec).pipe(
            map((data: BulkWellStatusChange[]) => {
                if (data.length > 0) return data[0];
                return null;
            })
        );
    }

    handleError(error: any): void {
        console.error(error);
        this.dialogService.showError();
    }


    home(): void {
        this.router.navigate(['/home']);
    }


    reject(): void {
        let dialog: Dialog = (this.request == null)
            ? this.notReadyDialog
            : this.confirmRejectDialog;
        dialog.toggleDialog(true);
        this.dialogService.updateDialog(dialog);
    }
}
